import { MessageKey } from '../../i18n';
import { DrillId, Stars } from '../../game/drill';
import { isUnlocked, Progress } from '../../game/progress';
import { CenterLine } from '../../sim/centerline';
import { FeatureDef } from '../../sim/features';
import { LevelDef } from '../../sim/level';
import { LEVELS, LevelId } from '../../sim/levels';

export const LEVEL_ORDER: readonly LevelId[] = ['ring', 'track', 'enduro'];
export const DRILL_ORDER: readonly DrillId[] = ['throttle', 'brake', 'figure8'];

/** Lap / route times (s) for 2 and 3 stars on track and enduro. Display only; unlocking uses PROGRESSION_CONFIG. */
export const TIME_STARS = { track: [155, 128], enduro: [540, 420] } as const; // track: MX Zielonka, 1.38 km; the test bot laps in ~150 s

export interface LevelCard {
  id: LevelId; locked: boolean; stars: number; maxStars: number; best: number | null; hint: MessageKey | null;
}

const timeStars = (t: number | null, [two, three]: readonly [number, number]): number =>
  t === null ? 0 : t < three ? 3 : t < two ? 2 : 1;

/** All levels open for play-testing; progression still drives stars, hints and the Ride suggestion. */
export const ALL_LEVELS_OPEN = true;

export function levelCards(p: Progress, openAll = ALL_LEVELS_OPEN): LevelCard[] {
  return LEVEL_ORDER.map((id) => {
    const locked = !openAll && !isUnlocked(p, id);
    if (id === 'ring') {
      const stars = DRILL_ORDER.reduce((a, d) => a + p.drills[d], 0);
      return { id, locked, stars, maxStars: 9, best: null, hint: null };
    }
    if (id === 'track') {
      return { id, locked, stars: timeStars(p.track.bestLap, TIME_STARS.track), maxStars: 3, best: p.track.bestLap, hint: locked ? 'unlock.trackHint' : null };
    }
    const best = p.enduro.finished > 0 ? p.enduro.bestTime : null;
    return { id, locked, stars: timeStars(best, TIME_STARS.enduro), maxStars: 3, best, hint: locked ? 'unlock.enduroHint' : null };
  });
}

export interface DrillCard { id: DrillId; stars: Stars; next: boolean }

/** `next` marks the first drill without three stars: what the screen suggests riding. */
export function drillCards(p: Progress): DrillCard[] {
  const next = DRILL_ORDER.find((d) => p.drills[d] < 3) ?? null;
  return DRILL_ORDER.map((id) => ({ id, stars: p.drills[id], next: id === next }));
}

/** The level the Levels screen highlights (Ride opens that screen): the furthest unlocked one with stars to win. */
export function suggestedLevel(p: Progress): LevelId {
  const open = levelCards(p, false).filter((c) => !c.locked);
  const unfinished = open.filter((c) => c.stars < c.maxStars);
  return (unfinished[unfinished.length - 1] ?? open[open.length - 1]!).id;
}

/** Drill after `id` in ring order, or null after the last. */
export const nextDrill = (id: DrillId): DrillId | null => DRILL_ORDER[DRILL_ORDER.indexOf(id) + 1] ?? null;

export type FeatureKind = FeatureDef['kind'];
/** The order obstacles are listed in on a card. */
export const FEATURE_ORDER: readonly FeatureKind[] = ['tabletop', 'double', 'roller', 'stepup', 'whoops', 'berm', 'roots', 'log', 'ford'];

export interface LevelStats { km: number | null; checkpoints: number; features: number; kinds: Partial<Record<FeatureKind, number>> }

/**
 * Loop length (km: two decimals under 2 km, so a 1.03 km lap does not read "1 km"; one decimal above), checkpoint
 * count and obstacles per kind, all from the level definition, so the cards cannot drift from the level data.
 */
export function levelStats(id: LevelId): LevelStats {
  const d: LevelDef = LEVELS[id];
  if (!d.track || d.map === 'drills') return { km: null, checkpoints: 0, features: 0, kinds: {} };
  const m = new CenterLine(d.track.points, 4).length;
  const km = m < 2000 ? Math.round(m / 10) / 100 : Math.round(m / 100) / 10;
  const kinds: Partial<Record<FeatureKind, number>> = {};
  for (const f of d.track.features) kinds[f.kind] = (kinds[f.kind] ?? 0) + 1;
  return { km, checkpoints: d.checkpoints.length, features: d.track.features.length, kinds };
}

/** "tabletops ×2, berms ×12, ford": obstacle kinds in card order, a lone one by its singular name. */
export function obstacleList(kinds: LevelStats['kinds'], tr: (key: MessageKey) => string): string {
  return FEATURE_ORDER.filter((k) => kinds[k]).map((k) => (kinds[k] === 1 ? tr(`ui.feat.one.${k}` as MessageKey) : `${tr(`ui.feat.${k}` as MessageKey)} ×${kinds[k]}`)).join(', ');
}
